import { Component, OnInit, OnDestroy, ViewEncapsulation, Input, OnChanges, SimpleChanges, ViewChild, Output, EventEmitter } from '@angular/core';
import { of, Subscription } from 'rxjs';
import { tap, map, switchMap, finalize } from 'rxjs/operators';
import { FilterService } from 'primeng/api';
import { Table, TableFilterEvent } from 'primeng/table';
import { saveAs } from 'file-saver';

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { DocumentListFilter } from 'src/app/shared/document-list-filter';
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { DocListEntry } from 'src/app/shared/doc-list-entry';
import { RestApiService } from 'src/app/services/rest-api.service';

/** Documents.DOCTYPE — invoice. The list's default when no docTypes input is given. */
const DOCTYPE_INVOICE: number = 1;

/** Documents.DOCTYPE — proforma invoice, added by the «Показывать коммерческие предложения» checkbox. */
const DOCTYPE_PROFORMA: number = 12;

@Component({
  selector: 'app-document-list-table',
  templateUrl: './document-list-table.component.html',
  styleUrls: ['./document-list-table.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class DocumentListTableComponent implements OnInit, OnDestroy, OnChanges {

  @Input() filters: DocumentListFilter[] = [];
  @Input() clientId: number = 0;
  // Document types to list. Left empty, the table shows invoices only
  // (DOCTYPE_INVOICE) — the same default as home-recent-orders-list.
  @Input() docTypes: number[] = [];
  // Empty by default so the toolbar renders exactly as home-recent-orders-list's
  // does (that component has no caption). A consumer that wants one passes it.
  @Input() title: string = '';
  @Input() today: Date = new Date();
  @Input() clean: boolean = true;

  @Output() selected = new EventEmitter<DocListEntry>();

  globalSettinsData: GlobalSettingsData;

  showProformaDocuments: boolean = false;
  documentList: DocListEntry[] = [];
  selectedDocument: DocListEntry = null;
  documentQuery: Subscription = null;
  totalDocuments: number = 0;

  loading: boolean = false;

  totalInvoiceSum: number = 0;
  totalPaidSum: number = 0;
  totalShippedSum: number = 0;

  @ViewChild('ptable1') ptable1: Table;

  cf1: any = {};    // to store f[string] filters value

  constructor(
    private restapi: RestApiService,
    private filterService: FilterService,
    private globalsettingsservice: GetglobalsettingsService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    console.log('*** DocumentListTableComponent ngOnChanges:');
    for (let propName in changes) {
      let change = changes[propName];

      switch (propName) {
        case 'clean':
          this.clearSavedSettings();

        case 'filters':
        case 'clientId':
        case 'docTypes':
          this.loadDocumentListData();
          break;
      }
    }
  }

  ngOnDestroy(): void {
    console.log("*** DocumentListTableComponent in ngOnDestroy");

    if (this.documentQuery) { this.documentQuery.unsubscribe(); }
  }

  ngOnInit(): void {
    console.log("*** DocumentListTableComponent in ngOnInit");

    this.today = new Date();

    console.log('*** DocumentListTableComponent in onInit: ' + JSON.stringify(this.today));

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this.filterService.register('gtOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value > filter))
    )
    );

    this.filterService.register('GteOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value >= filter))
    )
    );

    this.loadDocumentListData();
  }

  docListCalculatedFields(orderList: DocListEntry[]) {

    let jn: Date = new Date();
    let td: Date = new Date();
    td.setHours(0, 0, 0, 0);
    let tm: Date = new Date(td);
    tm.setHours(23, 59, 59);

    orderList.forEach((dc: DocListEntry) => {
      dc.fs = (dc.DOCTYPEID == 1) && (dc.DOCSUM > 0) && (Math.abs(dc.DOCSUM - dc.SHIPSUM) < 0.02);

      dc.ps = (dc.DOCTYPEID == 1) && (dc.DOCSUM > 0) && (dc.SHIPSUM > 0) && ((dc.DOCSUM - dc.SHIPSUM) > 0.02);

      dc.fp = (dc.DOCTYPEID == 1) && (dc.DOCSUM > 0) && (Math.abs(dc.DOCSUM - dc.PAYDSUM) < 0.02);

      dc.pp = (dc.DOCTYPEID == 1) && (dc.DOCSUM > 0) && (dc.PAYDSUM > 0) && (Math.abs(dc.DOCSUM - dc.PAYDSUM) > 0.02);

      dc.np = (dc.DOCTYPEID == 1) && (dc.DOCSUM > 0) && ((dc.SHIPSUM - dc.PAYDSUM) > 0.02);

      dc.go = (dc.DOCTYPEID == 1) && (new Date(dc.DOCVALDATE) < jn) && (dc.SHIPSUM == 0) && (dc.PAYDSUM == 0);

      dc.td = (new Date(dc.DOCDATE) > td) && (new Date(dc.DOCDATE) < tm);

      dc.fd = (new Date(dc.DOCDATE) > tm);
    });
  }

  private buildFilters(): DocumentListFilter[] {
    // Scoping filters — what narrows the list to a meaningful subset. The
    // doc-type filter below is NOT one of these: it is always present, so
    // counting it would defeat the "nothing to load yet" guard and turn an
    // unconfigured table into a query over every invoice in the database.
    const scopingFilters: DocumentListFilter[] = [...this.filters];

    if (this.clientId > 0) {
      scopingFilters.push({
        mode: 'DOCFIELD',
        value: {
          fieldName: 'DOCCLIENTSID',
          fieldValue: this.clientId
        }
      });
    }

    const hasDocTypes: boolean = !!this.docTypes && this.docTypes.length > 0;
    if (scopingFilters.length === 0 && !hasDocTypes) {
      return [];
    }

    // Document types. The base set is whatever the consumer asked for, and
    // invoices only when it asked for nothing — that default is what makes the
    // «Показывать коммерческие предложения» checkbox mean what its label says:
    // off shows invoices, on adds proforma invoices alongside them. This is the
    // behaviour home-recent-orders-list gets today from `_extended`, expressed
    // as a plain DOCTYPE filter.
    const baseTypes: number[] = hasDocTypes ? this.docTypes : [DOCTYPE_INVOICE];
    const docTypes: number[] = (this.showProformaDocuments && !baseTypes.includes(DOCTYPE_PROFORMA))
      ? [...baseTypes, DOCTYPE_PROFORMA]
      : baseTypes;

    return [...scopingFilters, { mode: 'DOCTYPE', value: docTypes }];
  }

  loadDocumentListData(): void {
    if (this.globalSettinsData) {
      const combinedFilters = this.buildFilters();

      // Guard: don't load if there's nothing to filter on
      if (combinedFilters.length === 0) {
        this.documentList = [];
        this.totalDocuments = 0;
        this.totalInvoiceSum = 0;
        this.totalPaidSum = 0;
        this.totalShippedSum = 0;
        return;
      }

      // ngOnChanges can fire several times in quick succession (a consumer that
      // sets clientId after its own async load, then adjusts docTypes), so drop
      // any in-flight request before starting the next one. The original only
      // reloaded on `today`/`clean` and never had overlapping calls to race.
      if (this.documentQuery) { this.documentQuery.unsubscribe(); }

      this.loading = true;
      this.documentQuery =
        of({ filters: combinedFilters })
        .pipe(
          switchMap(opt => this.restapi.getDocumentListByParameters(opt.filters).pipe(

            tap(data => { if (!environment.production) console.table(data); }),

            map(data => this.documentList = data || []),

            finalize(() => {
              this.totalDocuments = this.documentList.length;
              this.docListCalculatedFields(this.documentList);
              this.totalInvoiceSum = this.documentList.reduce((s, e) => s += e.DOCSUM, 0);
              this.totalPaidSum = this.documentList.reduce((s, e) => s += e.PAYDSUM, 0);
              this.totalShippedSum = this.documentList.reduce((s, e) => s += e.SHIPSUM, 0);
              this.loading = false;
            })
          )))
        .subscribe({
          error: (err) => {
            console.log('*** DocumentListTableComponent in loadDocumentListData error: ' + JSON.stringify(err));
            this.loading = false;
          }
        });
    }
  }

  onFilterEvent(event: TableFilterEvent) {
    console.log('*** DocumentListTableComponent onFilterEvent: ' + JSON.stringify(event));

    this.totalInvoiceSum = event.filteredValue.reduce((s, e) => s += e.DOCSUM, 0);
    this.totalPaidSum = event.filteredValue.reduce((s, e) => s += e.PAYDSUM, 0);
    this.totalShippedSum = event.filteredValue.reduce((s, e) => s += e.SHIPSUM, 0);
  }

  onChangeDocumentType(event) {
    console.log('*** DocumentListTableComponent in onChangeDocumentType');
    console.log(event);
    this.loadDocumentListData();
  }

  onRowSelect(event) {
    this.selected.emit(event.data);
  }

  onStateSave(state) {
    console.log('*** DocumentListTableComponent onStateSave: ' + JSON.stringify(state));
  }

  onStateRestoreNew(state) {
    console.log('*** DocumentListTableComponent onStateRestoreNew: ' + JSON.stringify(state));
    if (state.filters) {
      for (let f in state.filters) {
        let fltItem = state.filters[f];
        this.cf1[f] = Array.isArray(fltItem) ? fltItem[0].value : fltItem.value;
      }
    }
  }

  clearSavedSettings() {
    console.log('*** DocumentListTableComponent in clearSavedSettings: ' + this.clean);
    try {
      this.ptable1.clearState();
    } catch (e) {
      console.log('*** DocumentListTableComponent in clearSavedSettings: ' + JSON.stringify(e));
    }
    this.clean = false;
  }

  reloadTable(event) {
    console.log('*** DocumentListTableComponent reloadTable: ' + JSON.stringify(event));

    this.loadDocumentListData();
  }

  clearOrResetTableFilter(event) {
    console.log('*** DocumentListTableComponent clearOrResetTableFilter: ' + JSON.stringify(event));

    this.clearSavedSettings();
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver").then(FileSaver => {
      const EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });

      saveAs(data, fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION);
    });
  }

  onExportData() {
    console.log('*** DocumentListTableComponent onExportData: ')

    import("xlsx").then(xlsx => {

      environment.production || console.table(this.documentList);

      const exportData: any =
        this.documentList.map(e => ({

          'Документ': e.DOCNAME,
          'Номер': e.DOCNUMBER,
          'Дата': new Date(e.DOCDATE),
          'Покупатель': e.CLIENTSNAME,
          'Ответственный': e.PERSONNAME,
          'Продавец': e.LOCALCOMP,
          'Менеджер': e.AUTHOR,
          'Сумма': e.DOCSUM,
          'Оплачено': e.PAYDSUM,
          'Отгружено': e.SHIPSUM,
          'Валюта': e.CURSTR,
          'Курс': e.DOCRATEVAL,
          'Примечание продавца': e.DOCNOTE1,
          'Примечание покупателя': e.DOCNOTE2,

        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { documents: worksheet }, SheetNames: ['documents'] };

      const excelBuffer: any = xlsx.write(workbook, { bookType: "xlsx", type: "array" });

      this.saveAsExcelFile(excelBuffer, "documents");
    });
  }
}
